/**
 * `<MdmAndTimeSection>` — Medical Decision Making + Total Time. The Level of
 * service line and every total are derived here, never stored, so they cannot
 * contradict the billing chips or go stale after an edit.
 */
import {
  ActionIcon,
  Box,
  Button,
  Group,
  NumberInput,
  Stack,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core'
import { IconPlus, IconX } from '@tabler/icons-react'
import { Controller, useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import type { UseFieldArrayReturn } from 'react-hook-form'
import {
  computeTimeTotals,
  getAttestedCandidate,
  type ProgressNoteEditFormValues,
  type TimeEntryItem,
} from '../progressNoteEditSchema'
import { DictateFieldMic } from '../dictation/DictateFieldMic'
import { NoteHeading } from './NoteHeading'

type TimeField = 'providerTimeEntries' | 'careCoordinatorTimeEntries'

export interface MdmAndTimeSectionProps {
  readOnly: boolean
}

const minutesLabel = (minutes: number): string =>
  `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`

/** Minutes field. Mantine emits '' while empty, so coerce to keep totals numeric. */
function MinutesInput({
  value,
  onChange,
  onBlur,
}: {
  value: number
  onChange: (minutes: number) => void
  onBlur?: () => void
}) {
  return (
    <NumberInput
      size="xs"
      w={110}
      min={0}
      clampBehavior="strict"
      suffix=" min"
      value={value}
      onChange={(next) => onChange(Number(next) || 0)}
      onBlur={onBlur}
    />
  )
}

/** One `label: N minutes` line. `strong` marks a total (bold, ruled off above). */
function TimeRow({
  label,
  minutes,
  strong = false,
}: {
  label: string
  minutes: number
  strong?: boolean
}) {
  return (
    <Group
      justify="space-between"
      gap="xl"
      wrap="nowrap"
      pt={strong ? 6 : 0}
      style={strong ? { borderTop: '1px solid #e5e7eb' } : undefined}
    >
      <Text size="sm" c="dark.7" fw={strong ? 700 : 400}>
        {label}
      </Text>
      <Text size="sm" c="dark.7" fw={strong ? 700 : 400} style={{ whiteSpace: 'nowrap' }}>
        {minutesLabel(minutes)}
      </Text>
    </Group>
  )
}

/** Rows for one time group — `rows` comes from the parent's watch, not a second one. */
function TimeEntryList({
  name,
  rows,
  fieldArray,
  readOnly,
  addLabel,
}: {
  name: TimeField
  rows: TimeEntryItem[]
  fieldArray: UseFieldArrayReturn<ProgressNoteEditFormValues, `billingSuggestion.${TimeField}`>
  readOnly: boolean
  addLabel: string
}) {
  const { control, setValue } = useFormContext<ProgressNoteEditFormValues>()

  if (readOnly) {
    return (
      <Stack gap={4}>
        {rows.map((row) => (
          <TimeRow key={row.id} label={row.label} minutes={row.minutes} />
        ))}
      </Stack>
    )
  }

  return (
    <Stack gap="xs">
      {fieldArray.fields.map((field, idx) => (
        <Group key={field.id} gap="xs" wrap="nowrap" align="center">
          <TextInput
            size="xs"
            style={{ flex: 1 }}
            value={rows[idx]?.label ?? ''}
            onChange={(e) =>
              setValue(`billingSuggestion.${name}.${idx}.label`, e.currentTarget.value, {
                shouldDirty: true,
              })
            }
            placeholder="e.g. Chart and onboarding review"
          />
          <Controller
            control={control}
            name={`billingSuggestion.${name}.${idx}.minutes`}
            render={({ field: minutesField }) => (
              <MinutesInput
                value={minutesField.value}
                onChange={minutesField.onChange}
                onBlur={minutesField.onBlur}
              />
            )}
          />
          <ActionIcon
            variant="subtle"
            color="gray"
            size="sm"
            onClick={() => fieldArray.remove(idx)}
            aria-label="Remove time entry"
          >
            <IconX size={14} />
          </ActionIcon>
        </Group>
      ))}
      <Button
        size="xs"
        variant="light"
        color="blue"
        leftSection={<IconPlus size={14} />}
        onClick={() => fieldArray.append({ id: crypto.randomUUID(), label: '', minutes: 0 })}
        style={{ alignSelf: 'flex-start' }}
      >
        {addLabel}
      </Button>
    </Stack>
  )
}

export function MdmAndTimeSection({ readOnly }: MdmAndTimeSectionProps) {
  const { control } = useFormContext<ProgressNoteEditFormValues>()

  const mdm = useWatch({ control, name: 'summary.mdm' }) ?? ''
  const billing = useWatch({ control, name: 'billingSuggestion' })

  const providerField = useFieldArray({
    control,
    name: 'billingSuggestion.providerTimeEntries',
  })
  const coordinatorField = useFieldArray({
    control,
    name: 'billingSuggestion.careCoordinatorTimeEntries',
  })

  const faceToFaceMinutes = billing?.durationMinutes ?? 0
  const providerRows = billing?.providerTimeEntries ?? []
  const coordinatorRows = billing?.careCoordinatorTimeEntries ?? []
  const { sameDayMinutes, coordinatorMinutes, providerTotal, combinedTotal } = computeTimeTotals(
    faceToFaceMinutes,
    providerRows,
    coordinatorRows,
  )

  // Gated independently so coordinator-only time still surfaces the section.
  const showMdm = !readOnly || mdm.trim() !== ''
  const showProvider = !readOnly || providerTotal > 0
  const showCoordinator = !readOnly || coordinatorRows.length > 0
  const showTotalTime = showProvider || showCoordinator

  if (!showMdm && !showTotalTime) return null

  const attested = getAttestedCandidate(billing)

  return (
    <Box mb="xl">
      <Stack gap="lg">
        {showMdm && (
          <Box>
            <NoteHeading level="section" id="mdm" label="Medical Decision Making" />
            {readOnly ? (
              <Text size="sm" c="dark.7" style={{ lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                {mdm}
              </Text>
            ) : (
              <Controller
                control={control}
                name="summary.mdm"
                render={({ field }) => (
                  <Textarea
                    {...field}
                    autosize
                    minRows={3}
                    placeholder="MDM – Moderate complexity. Problems: … Data: … Risk: …"
                    data-testid="mdm-narrative"
                    rightSectionPointerEvents="all"
                    rightSection={
                      <DictateFieldMic
                        field={{ path: 'summary.mdm', label: 'Medical Decision Making' }}
                        testId="mdm-narrative"
                      />
                    }
                  />
                )}
              />
            )}
            {attested && (
              <Text size="sm" c="dark.7" mt="xs" style={{ lineHeight: 1.7 }}>
                <Text component="span" fw={700}>
                  Level of service:
                </Text>{' '}
                {attested.code}
                {attested.display ? ` — ${attested.display}` : ''}
              </Text>
            )}
          </Box>
        )}

        {showTotalTime && (
          <Box>
            <NoteHeading level="section" id="total-time" label="Total Time" />
            <Stack gap="md">
              {showProvider && (
                <Stack gap={4}>
                  {readOnly && faceToFaceMinutes > 0 && (
                    <TimeRow label="Face-to-face telehealth visit" minutes={faceToFaceMinutes} />
                  )}
                  {/* Editable: the session duration can be an appointment-length
                      fallback when Zoom reported none, and the provider must be
                      able to correct it before signing. Writes the same field
                      the Billing modal edits, so the two can't diverge. */}
                  {!readOnly && (
                    <Group justify="space-between" gap="xl" wrap="nowrap" align="center">
                      <Text size="sm" c="dark.7">
                        Face-to-face telehealth visit
                      </Text>
                      <Controller
                        control={control}
                        name="billingSuggestion.durationMinutes"
                        render={({ field }) => (
                          <MinutesInput
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                          />
                        )}
                      />
                    </Group>
                  )}
                  {(sameDayMinutes > 0 || !readOnly) && (
                    <TimeRow label="Same-day provider work" minutes={sameDayMinutes} />
                  )}
                  <Box pl="md">
                    <TimeEntryList
                      name="providerTimeEntries"
                      rows={providerRows}
                      fieldArray={providerField}
                      readOnly={readOnly}
                      addLabel="Add provider time"
                    />
                  </Box>
                  <TimeRow label="Provider total" minutes={providerTotal} strong />
                </Stack>
              )}

              {showCoordinator && (
                <Box>
                  <NoteHeading
                    level="sub"
                    id="care-coordinator-time"
                    label="Care-Coordinator Time"
                  />
                  <Stack gap={4}>
                    <TimeEntryList
                      name="careCoordinatorTimeEntries"
                      rows={coordinatorRows}
                      fieldArray={coordinatorField}
                      readOnly={readOnly}
                      addLabel="Add care-coordinator time"
                    />
                    <TimeRow label="Care-coordinator total" minutes={coordinatorMinutes} strong />
                  </Stack>
                </Box>
              )}

              {coordinatorRows.length > 0 && (
                <Text size="xs" c="dimmed" style={{ lineHeight: 1.6 }}>
                  Combined team workload: {minutesLabel(combinedTotal)}. The combined workload is
                  shown for operational tracking and is not the provider&apos;s E/M billing time.
                </Text>
              )}
            </Stack>
          </Box>
        )}
      </Stack>
    </Box>
  )
}
